import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  ExpandMore,
  Business,
  LocationOn,
  CalendarToday,
  TrendingUp,
  TrendingDown,
  TrackChanges,
  AccountTree,
  BarChart,
  Edit,
  Delete, Add,
} from "@mui/icons-material";
import { Client, Organization } from "./types";
import { LocationModel } from "object-models/location-model";
import { useNavigate } from "react-router-dom";
import AlertDialog from "components/AlertDialog";
import { ConfirmationMessages, UserRoles } from "constants/constant";
import SnackBar from "components/SnackBar/SnackBar";
import { _deleteClient } from "middlewares/ClientApi/client";
import { _deleteOrganization } from "middlewares/OrganizationApi/organization";
import { _deleteLocation } from "middlewares/LocationApi/location";
import { AlertsProps } from "components/Alerts/data/Alerts.type";

import AddLocationAltOutlinedIcon from '@mui/icons-material/AddLocationAltOutlined';
import DomainAddOutlinedIcon from '@mui/icons-material/DomainAddOutlined';
import { useAppState } from "context/appState.context";
import OrganizationReductionMeasureDialog from "./OrganizationReductionMeasureDialog";

// Constants for repeated styles
const ACCORDION_SX = {
  mb: 1,
  "&:before": { display: "none" },
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  borderRadius: "8px !important",
  "&.Mui-expanded": {
    margin: "0 0 8px 0",
  },
};

const ACCORDION_SUMMARY_SX = {
  "&:hover": {
    bgcolor: "action.hover",
  },
  borderRadius: "8px",
};

const ICON_BUTTON_ACTION_SX = {
  color: "primary.main",
  padding: "4px",
};

const INITIAL_ALERT_STATE: AlertsProps = {
  show: false,
  severity: "info",
  message: "",
};

const VIEWPORT_ANCHOR_DURATION_MS = 700;

interface ClientListViewProps {
  clients: Client[];
  expandedClientId?: number | null;
  expandedOrgId?: number | null;
  onClientClick?: (client: Client) => void;
  onClientPortfolioClick?: (client: Client) => void;
  onOrganizationPortfolioClick?: (client: Client, organization: Organization) => void;
  onEditClick?: (client: Client) => void;
  onClientDeleted?: (clientId: number) => void;
  onSetTargetClick?: (client: Client) => void;
  onClientExpand?: (client: Client | null) => void;
  onOrgExpand?: (org: Organization | null, client: Client | null) => void;
  onOrgEditClick?: (org: Organization) => void;
  onOrgDeleted?: (orgId: number) => void;
  onLocationEditClick?: (location: LocationModel) => void;
  onLocationDeleted?: (locationId: number) => void;
  onOrgAddClick?: (client: Client) => void;
  onLocationAddClick?: (org: Organization) => void;
  onSetTargetOrgClick?: (org: Organization) => void;
}

const ClientListView: React.FC<ClientListViewProps> = ({
  clients,
  expandedClientId,
  expandedOrgId,
  onClientClick,
  onClientPortfolioClick,
  onOrganizationPortfolioClick,
  onEditClick,
  onClientDeleted,
  onSetTargetClick,
  onClientExpand,
  onOrgExpand,
  onOrgEditClick,
  onOrgDeleted,
  onLocationEditClick,
  onLocationDeleted,
  onOrgAddClick,
  onLocationAddClick,
  onSetTargetOrgClick,
}) => {
  const [expandedClient, setExpandedClient] = useState<number | null>(expandedClientId ?? null);
  const [expandedOrg, setExpandedOrg] = useState<number | null>(expandedOrgId ?? null);
  const viewportAnchorFrameRef = useRef<number | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    clientId: number | null;
    clientName: string;
  }>({
    open: false,
    clientId: null,
    clientName: "",
  });
  const [deleteOrgModal, setDeleteOrgModal] = useState<{
    open: boolean;
    orgId: number | null;
    orgName: string;
  }>({
    open: false,
    orgId: null,
    orgName: "",
  });
  const [deleteLocationModal, setDeleteLocationModal] = useState<{
    open: boolean;
    locationId: number | null;
    locationName: string;
  }>({
    open: false,
    locationId: null,
    locationName: "",
  });
  const [success, setSuccess] = useState<AlertsProps>(INITIAL_ALERT_STATE);
  const [reductionMeasureOrg, setReductionMeasureOrg] = useState<Organization | null>(null);
  const navigate = useNavigate();
  const toolbarControlSx = {
    textTransform: "none",
    fontSize: "0.76rem",
    fontWeight: 500,
    py: 0.45,
    px: 1.4,
    minHeight: 32,
    borderRadius: "6px",
  };

  const { userRole } = useAppState();

  useEffect(() => {
    setExpandedClient(expandedClientId ?? null);
  }, [expandedClientId]);

  useEffect(() => {
    setExpandedOrg(expandedOrgId ?? null);
  }, [expandedOrgId]);

  useEffect(() => () => {
    if (viewportAnchorFrameRef.current !== null) {
      cancelAnimationFrame(viewportAnchorFrameRef.current);
    }
  }, []);

  const preserveSummaryViewportPosition = useCallback((event: React.SyntheticEvent) => {
    const summary = event.target instanceof Element
      ? event.target.closest<HTMLElement>(".MuiAccordionSummary-root")
      : null;
    if (!summary) return;

    if (viewportAnchorFrameRef.current !== null) {
      cancelAnimationFrame(viewportAnchorFrameRef.current);
    }

    const initialTop = summary.getBoundingClientRect().top;
    let startTime: number | null = null;

    const maintainPosition = (timestamp: number) => {
      if (!summary.isConnected) {
        viewportAnchorFrameRef.current = null;
        return;
      }

      if (startTime === null) {
        startTime = timestamp;
      }

      const offset = summary.getBoundingClientRect().top - initialTop;
      if (Math.abs(offset) > 0.5) {
        window.scrollBy(0, offset);
      }

      if (timestamp - startTime < VIEWPORT_ANCHOR_DURATION_MS) {
        viewportAnchorFrameRef.current = requestAnimationFrame(maintainPosition);
      } else {
        viewportAnchorFrameRef.current = null;
      }
    };

    viewportAnchorFrameRef.current = requestAnimationFrame(maintainPosition);
  }, []);

  const handleClientExpand = useCallback((clientId: number, event: React.SyntheticEvent) => {
    preserveSummaryViewportPosition(event);
    const next = expandedClient === clientId ? null : clientId;
    const client = next !== null ? clients.find((c) => c.clientId === next) ?? null : null;
    setExpandedClient(next);
    setExpandedOrg(null);
    onClientExpand?.(client);
    onOrgExpand?.(null, client);
  }, [expandedClient, clients, onClientExpand, onOrgExpand, preserveSummaryViewportPosition]);

  const handleOrgExpand = useCallback(
    (orgId: number, event: React.SyntheticEvent, client: Client) => {
      event.stopPropagation();
      preserveSummaryViewportPosition(event);
      const next = expandedOrg === orgId ? null : orgId;
      const org = next !== null
        ? client.organizations?.find((o) => o.orgId === next) ?? null
        : null;
      setExpandedOrg(next);
      // Always keep the expanded client in breadcrumbs, even when collapsing an org
      onOrgExpand?.(org, client);
    },
    [expandedOrg, onOrgExpand, preserveSummaryViewportPosition],
  );

  const handleLocationClick = useCallback(
    (locationId: number, client: Client, org: Organization) => {
      localStorage.removeItem("subAreaName");
      localStorage.setItem("LocationId", String(locationId));
      localStorage.setItem("Portfolioview", "0");
      localStorage.setItem("breadcrumb_clientName", client.clientName);
      localStorage.setItem("breadcrumb_clientId", String(client.clientId));
      localStorage.setItem("breadcrumb_orgName", org.orgName);
      localStorage.setItem("breadcrumb_orgId", String(org.orgId));
      navigate(`/Location/${locationId}/Location-Details`);
    },
    [navigate],
  );

  const handleLocationRowClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, client: Client, org: Organization) => {
      const locationId = Number(event.currentTarget.dataset.locationId);
      if (!Number.isNaN(locationId)) {
        handleLocationClick(locationId, client, org);
      }
    },
    [handleLocationClick],
  );

  const handleLocationRowKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, client: Client, org: Organization) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        const locationId = Number(event.currentTarget.dataset.locationId);
        if (!Number.isNaN(locationId)) {
          handleLocationClick(locationId, client, org);
        }
      }
    },
    [handleLocationClick],
  );

  const handleDelete = useCallback(
    (clientId: number, e: React.MouseEvent, clientName?: string) => {
      e.stopPropagation();
      setDeleteModal({
        open: true,
        clientId,
        clientName: clientName ?? "",
      });
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    setDeleteModal({ open: false, clientId: null, clientName: "" });
  }, []);

  const showAlert = useCallback((message: string, severity: "success" | "error") => {
    setSuccess({ show: true, severity, message });
  }, []);

  const handleOrgDelete = useCallback(
    (orgId: number, e: React.MouseEvent, orgName?: string) => {
      e.stopPropagation();
      setDeleteOrgModal({ open: true, orgId, orgName: orgName ?? "" });
    },
    [],
  );

  const handleCloseOrgModal = useCallback(() => {
    setDeleteOrgModal({ open: false, orgId: null, orgName: "" });
  }, []);

  const deleteOrg = useCallback(async () => {
    if (deleteOrgModal.orgId === null) return;
    try {
      const res = await _deleteOrganization(deleteOrgModal.orgId);
      if (res) {
        onOrgDeleted?.(deleteOrgModal.orgId);
        showAlert("Deleted Successfully!", "success");
      }
    } catch (err) {
      console.error("Delete org error:", err);
      showAlert("Failed to delete organization", "error");
    }
  }, [deleteOrgModal.orgId, onOrgDeleted, showAlert]);

  const handleConfirmOrgDelete = useCallback(async () => {
    await deleteOrg();
    handleCloseOrgModal();
  }, [deleteOrg, handleCloseOrgModal]);

  const handleLocationDeleteClick = useCallback(
    (locationId: number, e: React.MouseEvent, locationName?: string) => {
      e.stopPropagation();
      setDeleteLocationModal({ open: true, locationId, locationName: locationName ?? "" });
    },
    [],
  );

  const handleCloseLocationModal = useCallback(() => {
    setDeleteLocationModal({ open: false, locationId: null, locationName: "" });
  }, []);

  const deleteLocationItem = useCallback(async () => {
    if (deleteLocationModal.locationId === null) return;
    try {
      const res = await _deleteLocation(deleteLocationModal.locationId);
      if (res) {
        onLocationDeleted?.(deleteLocationModal.locationId);
        showAlert("Deleted Successfully!", "success");
      }
    } catch (err) {
      console.error("Delete location error:", err);
      showAlert("Failed to delete location", "error");
    }
  }, [deleteLocationModal.locationId, onLocationDeleted, showAlert]);

  const handleConfirmLocationDelete = useCallback(async () => {
    await deleteLocationItem();
    handleCloseLocationModal();
  }, [deleteLocationItem, handleCloseLocationModal]);

  const deleteClient = useCallback(async () => {
    if (deleteModal.clientId === null) return;

    try {
      const res = await _deleteClient(deleteModal.clientId);
      if (res) {
        onClientDeleted?.(deleteModal.clientId);
        showAlert("Deleted Successfully!", "success");
      }
    } catch (err) {
      console.error("Delete error:", err);
      showAlert("Failed to delete client", "error");
    }
  }, [deleteModal.clientId, onClientDeleted, showAlert]);

  const handleConfirm = useCallback(async () => {
    await deleteClient();
    handleCloseModal();
  }, [deleteClient, handleCloseModal]);

  const getTotalEmissions = useCallback((location: LocationModel) => {
    const total =
      (location.scope1GHGEmission ?? 0) +
      (location.scope2GHGEmission ?? 0) +
      (location.scope3GHGEmission ?? 0);
    return total.toLocaleString("en-US", { maximumFractionDigits: 0 });
  }, []);

  return (
    <Box>
      {clients.map((client) => (
        <Accordion
          key={client.clientId}
          expanded={expandedClient === client.clientId}
          onChange={(event) => handleClientExpand(client.clientId, event)}
          TransitionProps={{ timeout: 0 }}
          sx={ACCORDION_SX}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={ACCORDION_SUMMARY_SX}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: 2,
              }}
            >
              <Business sx={{ color: "primary.main", fontSize: 28 }} />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, fontSize: "1rem" }}
                >
                  {client.clientName}
                </Typography>
                <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
                  <Chip
                    label={client.clientType}
                    size="small"
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <Business sx={{ fontSize: 14 }} />
                    {client.organizations?.length ?? 0} Organization
                    {(client.organizations?.length ?? 0) > 1 ? "s" : ""}
                  </Typography>
                </Box>
              </Box>
              {client.organizations?.some((org) => (org.locations?.length ?? 0) > 0) && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<BarChart sx={{ fontSize: 16 }} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClientPortfolioClick?.(client);
                  }}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.76rem",
                    fontWeight: 500,
                    py: 0.45,
                    px: 1.4,
                    minHeight: 32,
                    borderRadius: "6px",
                    borderColor: "#E57A1F",
                    color: "#E57A1F",
                    backgroundColor: "#fff7ef",
                    "&:hover": {
                      borderColor: "#D26A11",
                      backgroundColor: "#fff1e4",
                    },
                  }}
                >
                  Client Portfolio
                </Button>
              )}
              {(userRole === UserRoles.SuperAdmin || userRole === UserRoles.Admin) && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title="Add Organization">
                    <IconButton
                      size="small"
                      aria-label="Add Organization"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (userRole === "Super Admin" || userRole === "Admin") {
                          onOrgAddClick?.(client);
                        }
                      }}
                      sx={{ color: "primary.main" }}
                    >
                      <DomainAddOutlinedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                </Box> 
              )}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Set Target">
                  <IconButton
                    size="small"
                    aria-label="Set Target"
                    data-testid="btnSetTarget"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSetTargetClick?.(client);
                    }}
                    sx={{ color: "primary.main" }}
                  >
                    <TrackChanges sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    aria-label="Edit client"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditClick?.(client);
                    }}
                    sx={{ color: "warning.main" }}
                  >
                    <Edit sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    aria-label="Delete client"
                    onClick={(e) => handleDelete(client.clientId, e, client.clientName)}
                    sx={{ color: "error.main" }}
                  >
                    <Delete sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2 }}>
            {client.organizations && client.organizations.length > 0 ? (
              <Box>
                <Divider sx={{ mb: 1 }} />
                {client.organizations.map((org) => (
                  <Accordion
                    key={org.orgId}
                    expanded={expandedOrg === org.orgId}
                    onChange={(e) => handleOrgExpand(org.orgId, e, client)}
                    sx={{
                      mb: 1,
                      boxShadow: "none",
                      border: "1px solid",
                      borderColor: "divider",
                      "&:before": { display: "none" },
                      "&.Mui-expanded": {
                        margin: "0 0 8px 0",
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{
                        minHeight: 48,
                        "&.Mui-expanded": {
                          minHeight: 48,
                        },
                        "& .MuiAccordionSummary-content": {
                          my: 1,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          gap: 2,
                        }}
                      >
                        <Business
                          sx={{ color: "secondary.main", fontSize: 24 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                          >
                            {org.orgName}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 2, mt: 0.25 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <LocationOn sx={{ fontSize: 12 }} />
                              {org.locations?.length ?? 0} Location
                              {(org.locations?.length ?? 0) > 1 ? "s" : ""}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {org.country}
                            </Typography>
                          </Box>
                        </Box>
                        {(org.locations?.length ?? 0) > 0 && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AccountTree sx={{ fontSize: 16 }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              onOrganizationPortfolioClick?.(client, org);
                            }}
                            sx={{
                              textTransform: "none",
                              fontSize: "0.76rem",
                              fontWeight: 500,
                              py: 0.45,
                              px: 1.4,
                              minHeight: 32,
                              borderRadius: "6px",
                              borderColor: "#E57A1F",
                              color: "#E57A1F",
                              backgroundColor: "#fff7ef",
                              "&:hover": {
                                borderColor: "#D26A11",
                                backgroundColor: "#fff1e4",
                              },
                            }}
                          >
                            Organization Portfolio
                          </Button>
                        )}
                        <Box sx={{ display: "flex", gap: 1 }}>
                          {(userRole === UserRoles.SuperAdmin || userRole === UserRoles.Admin) && (
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Tooltip title="Add Location">
                                <IconButton
                                  size="small"
                                  aria-label="Add Location"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (userRole === "Super Admin" || userRole === "Admin") {
                                      onLocationAddClick?.(org);
                                    }
                                  }}
                                  sx={{ color: "primary.main" }}
                                >
                                  <AddLocationAltOutlinedIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                          <Tooltip title="Set Target">
                              <IconButton
                                size="small"
                                aria-label="Set Target"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSetTargetOrgClick?.(org);
                                }}
                                sx={{ color: "primary.main" }}
                              >
                                <TrackChanges sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          <Tooltip title="Reduction Measures">
                            <IconButton
                              size="small"
                              aria-label="Reduction Measures"
                              onClick={(e) => {
                                e.stopPropagation();
                                setReductionMeasureOrg(org);
                              }}
                              sx={{ color: "primary.main" }}
                            >
                              <TrendingDown sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              aria-label="Edit organization"
                              onClick={(e) => { e.stopPropagation(); onOrgEditClick?.(org); }}
                              sx={{ ...ICON_BUTTON_ACTION_SX, color: "warning.main" }}
                            >
                              <Edit sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              aria-label="Delete organization"
                              onClick={(e) => handleOrgDelete(org.orgId, e, org.orgName)}
                              sx={{ ...ICON_BUTTON_ACTION_SX, color: "error.main" }}
                            >
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                      {org.locations && org.locations.length > 0 ? (
                        <Box sx={{ p: 2 }}>
                          {org.locations.map((location) => (
                            <Box
                              key={location.locationId}
                              sx={{
                                mb: 1,
                                p: 1.5,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                width: "100%",
                                "&:last-child": {
                                  mb: 0,
                                },
                              }}
                            >
                              <Box
                                component="button"
                                type="button"
                                data-testid={`location-row-${location.locationId}`}
                                data-location-id={location.locationId}
                                aria-label={`Open location ${location.locationName ?? location.locationId}`}
                                onClick={(e) => handleLocationRowClick(e, client, org)}
                                onKeyDown={(e) => handleLocationRowKeyDown(e, client, org)}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                  flex: 1,
                                  width: "100%",
                                  border: 0,
                                  p: 0,
                                  m: 0,
                                  backgroundColor: "transparent",
                                  textAlign: "left",
                                  cursor: "pointer",
                                  "&:hover": {
                                    bgcolor: "action.hover",
                                  },
                                  "&:focus-visible": {
                                    outline: "2px solid",
                                    outlineColor: "primary.main",
                                    outlineOffset: 1,
                                  },
                                }}
                              >
                                <LocationOn sx={{ color: "secondary.main", fontSize: 22 }} />
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 600, fontSize: "0.95rem" }}
                                  >
                                    {location.locationName ?? "Location Name"}
                                  </Typography>
                                  <Box sx={{ display: "flex", gap: 2, mt: 0.25 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                                    >
                                      <TrendingUp sx={{ fontSize: 13 }} />
                                      {getTotalEmissions(location)} tCO₂e
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                                    >
                                      <CalendarToday sx={{ fontSize: 13 }} />
                                      Base: {location.baseYear ?? "N/A"}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    data-testid={`location-edit-${location.locationId}`}
                                    aria-label="Edit location"
                                    onClick={(e) => { e.stopPropagation(); onLocationEditClick?.(location); }}
                                    sx={{ ...ICON_BUTTON_ACTION_SX, color: "warning.main" }}
                                  >
                                    <Edit sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    data-testid={`location-delete-${location.locationId}`}
                                    aria-label="Delete location"
                                    onClick={(e) => { e.stopPropagation(); handleLocationDeleteClick(location.locationId, e, location.locationName ?? ""); }}
                                    sx={{ ...ICON_BUTTON_ACTION_SX, color: "error.main" }}
                                  >
                                    <Delete sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ pl: 4, py: 1 }}
                        >
                          No locations available
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 2, textAlign: "center" }}
              >
                No organizations available
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
      <AlertDialog
        open={deleteModal.open}
        openDialog={() => setDeleteModal({ ...deleteModal, open: true })}
        handleClose={handleCloseModal}
        handleConfirm={handleConfirm}
        locationName={deleteModal.clientName}
        confirmationMessage={ConfirmationMessages.clientConfirmation}
      />
      <AlertDialog
        open={deleteOrgModal.open}
        openDialog={() => setDeleteOrgModal({ ...deleteOrgModal, open: true })}
        handleClose={handleCloseOrgModal}
        handleConfirm={handleConfirmOrgDelete}
        locationName={deleteOrgModal.orgName}
        confirmationMessage={ConfirmationMessages.orgConfirmation}
      />
      <AlertDialog
        open={deleteLocationModal.open}
        openDialog={() => setDeleteLocationModal({ ...deleteLocationModal, open: true })}
        handleClose={handleCloseLocationModal}
        handleConfirm={handleConfirmLocationDelete}
        locationName={deleteLocationModal.locationName}
        confirmationMessage={ConfirmationMessages.locationConfirmation}
      />
      <OrganizationReductionMeasureDialog
        open={reductionMeasureOrg !== null}
        organization={reductionMeasureOrg}
        onClose={() => setReductionMeasureOrg(null)}
        showSnackbar={setSuccess}
      />
      <SnackBar
        show={success.show}
        message={success.message}
        severity={success.severity}
        onclose={() => setSuccess(INITIAL_ALERT_STATE)}
      />
    </Box>
  );
};

export default ClientListView;
